import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  ClipboardCheck,
  Download,
  ExternalLink,
  Eye,
  FileText,
  FileUp,
  FileWarning,
  HeartPulse,
  Loader2,
  Plus,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { farmGet, farmPost } from "./shared";
import ControlloRicette from "./ControlloRicette";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

const FILTERS = [
  { id: "tutte", label: "Tutte" },
  { id: "in_struttura", label: "In struttura" },
  { id: "clienti_diretti", label: "Clienti diretti" },
  { id: "da_verificare", label: "Da verificare" },
  { id: "documenti_richiesti", label: "Documenti richiesti" },
  { id: "completate", label: "Completate" },
];

const COLUMNS = [
  {
    id: "carepy",
    label: "Carepy ricevuti",
    accent: "bg-emerald-500",
    header: "text-emerald-700",
  },
  {
    id: "terapie",
    label: "Terapie da visionare",
    accent: "bg-amber-500",
    header: "text-amber-700",
  },
  {
    id: "ricette",
    label: "Ricette da verificare",
    accent: "bg-violet-500",
    header: "text-violet-700",
  },
  {
    id: "documenti",
    label: "Documenti richiesti",
    accent: "bg-teal-500",
    header: "text-teal-700",
  },
  {
    id: "completate",
    label: "Pratiche completate",
    accent: "bg-slate-400",
    header: "text-slate-600",
  },
];

function initials(nome = "", cognome = "") {
  const a = (cognome || "").trim()[0] || "";
  const b = (nome || "").trim()[0] || "";
  return `${a}${b}`.toUpperCase() || "?";
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function ageFromCf(cf) {
  if (!cf || cf.length < 6) return null;
  try {
    let yy = parseInt(cf.slice(1, 3), 10);
    if (Number.isNaN(yy)) return null;
    const nowY = new Date().getFullYear() % 100;
    const century = yy <= nowY ? 2000 : 1900;
    return new Date().getFullYear() - (century + yy);
  } catch {
    return null;
  }
}

function assignColumn(p) {
  if (p.stato === "completata" || p.columnHint === "completate") return "completate";
  if (p.terapia_da_visionare) return "terapie";
  if ((p.anomalie_documentali || []).length > 0) return "documenti";
  if (p.stato === "pronta_invio") return "ricette";
  if (
    p.richiesta_farmacia_ricevuta &&
    !p.ricette_medico_ricevute &&
    p.stato !== "completata"
  ) {
    return "ricette";
  }
  if ((p.carepy_count || 0) > 0 || p.stato === "attesa_medico" || p.has_carepy) {
    return "carepy";
  }
  if ((p.ricette_count || 0) > 0 && !p.ricette_medico_ricevute) return "ricette";
  if (p.stato === "bloccata" || p.stato === "nessuna_pratica") return "documenti";
  return "carepy";
}

function cardStatusLabel(p, colId) {
  if (colId === "completate") return { text: "Pratica chiusa", tone: "text-emerald-700" };
  if (colId === "terapie") return { text: "Terapia da controllare", tone: "text-amber-700" };
  if (colId === "documenti") {
    const a = (p.anomalie_documentali || [])[0];
    return {
      text: a ? String(a).replace(/_/g, " ") : "Documento mancante",
      tone: "text-rose-600",
    };
  }
  if (colId === "ricette") {
    if (p.ricette_medico_ricevute) return { text: "Ricette ricevute", tone: "text-violet-700" };
    return { text: "Ricetta mancante", tone: "text-rose-600" };
  }
  if ((p.carepy_count || 0) > 0) return { text: "Carepy caricato", tone: "text-emerald-700" };
  return { text: "In lavorazione", tone: "text-slate-600" };
}

function KpiTile({ icon: Icon, label, value, tone }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    teal: "bg-teal-50 text-teal-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.slate}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function ChannelDonut({ inStruttura, clienti }) {
  const total = inStruttura + clienti;
  const pctStr = total ? Math.round((inStruttura / total) * 100) : 0;
  const pctCli = total ? 100 - pctStr : 0;
  const gradient = total
    ? `conic-gradient(#0F5C4C 0 ${pctStr}%, #3b82f6 ${pctStr}% 100%)`
    : "conic-gradient(#e2e8f0 0 100%)";

  return (
    <div className="flex items-center gap-4">
      <div
        className="relative h-24 w-24 shrink-0 rounded-full"
        style={{ background: gradient }}
        aria-hidden
      >
        <div className="absolute inset-3 flex flex-col items-center justify-center rounded-full bg-white">
          <span className="text-lg font-semibold text-slate-900">{total}</span>
          <span className="text-[10px] text-slate-400">totali</span>
        </div>
      </div>
      <ul className="space-y-2 text-xs">
        <li className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#0F5C4C]" />
          <span className="text-slate-600">In struttura</span>
          <span className="ml-auto font-semibold tabular-nums text-slate-900">
            {inStruttura} · {pctStr}%
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
          <span className="text-slate-600">Clienti diretti</span>
          <span className="ml-auto font-semibold tabular-nums text-slate-900">
            {clienti} · {pctCli}%
          </span>
        </li>
      </ul>
    </div>
  );
}

function PraticaDetailPanel({ pratica, onClose, onConfirmTerapia }) {
  const navigate = useNavigate();
  if (!pratica) {
    return (
      <aside className="hidden w-[320px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <ClipboardCheck className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Pratica selezionata</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una card dal flusso per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const nome = `${pratica.cognome || ""} ${pratica.nome || ""}`.trim() || "Assistito";
  const isCliente = pratica.tipologia === "cliente_diretto";
  const age = ageFromCf(pratica.codice_fiscale);
  const schedaTo = isCliente
    ? `/farmacia/ospite/${encodeURIComponent(pratica.cliente_id)}?tipo=cliente`
    : `/farmacia/ospite/${encodeURIComponent(pratica.fascicolo_id)}`;
  const docs = pratica.anomalie_documentali || [];
  const controlloParams = new URLSearchParams({ vista: "copertura" });
  if (pratica.organization_id) controlloParams.set("organization_id", pratica.organization_id);
  if (pratica.codice_fiscale || nome) controlloParams.set("q", pratica.codice_fiscale || nome);
  if (pratica.richiesta_id) controlloParams.set("richiesta_id", pratica.richiesta_id);
  const controlloTo = `/farmacia/pratiche?${controlloParams.toString()}`;
  const carepyTo = pratica.organization_id && pratica.fascicolo_id
    ? `/farmacia/richieste?organization_id=${encodeURIComponent(pratica.organization_id)}&fascicolo_id=${encodeURIComponent(pratica.fascicolo_id)}`
    : "/farmacia/richieste";

  return (
    <aside className="w-full shrink-0 xl:w-[320px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-sm font-semibold text-white">
              {initials(pratica.nome, pratica.cognome)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="truncate text-sm font-semibold text-slate-900">{nome}</h3>
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
                  onClick={onClose}
                >
                  Chiudi
                </button>
              </div>
              <Badge
                variant="outline"
                className={`mt-1.5 text-[10px] ${
                  isCliente
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-sky-200 bg-sky-50 text-sky-700"
                }`}
              >
                {isCliente ? "Cliente diretto" : "In struttura"}
              </Badge>
              <p className="mt-2 space-y-0.5 text-xs text-slate-500">
                {age != null ? <span className="block">{age} anni</span> : null}
                <span className="block">Medico: {pratica.medico || "—"}</span>
                <span className="block truncate">
                  {isCliente
                    ? "Canale: Consegna a domicilio"
                    : pratica.struttura_nome || pratica.struttura || "—"}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Stato pratica
            </p>
            <ul className="space-y-1.5 text-xs">
              <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                <span className="text-slate-600">Carepy</span>
                <span className="font-medium text-slate-800">
                  {(pratica.carepy_count || 0) > 0 ? "Caricato" : "Assente"}
                </span>
              </li>
              <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                <span className="text-slate-600">Terapia</span>
                <span className="font-medium text-slate-800">
                  {pratica.terapia_da_visionare
                    ? "Da visionare"
                    : pratica.terapia_presente || (pratica.terapie_count || 0) > 0
                      ? "Presente"
                      : "Assente"}
                </span>
              </li>
              <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                <span className="text-slate-600">Ricette</span>
                <span className="font-medium text-slate-800">
                  {pratica.ricette_medico_ricevute
                    ? `${pratica.ricette_count || 0} ricevute`
                    : "Mancanti"}
                </span>
              </li>
              <li className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2">
                <span className="text-slate-600">Documenti</span>
                <span className="font-medium text-slate-800">
                  {docs.length > 0 ? `${docs.length} da richiedere` : "Ok"}
                </span>
              </li>
            </ul>
          </div>
          {pratica.tipologia === "in_struttura" && pratica.terapia_da_visionare ? (
            <Button size="sm" className="w-full bg-[#0F5C4C] hover:bg-[#0c4a3e]" onClick={() => onConfirmTerapia?.(pratica.fascicolo_id)}>
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Conferma presa visione terapia
            </Button>
          ) : null}

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Documenti pratica
            </p>
            <ul className="space-y-1.5">
              {docs.length === 0 ? (
                <li className="rounded-lg border border-slate-100 px-2.5 py-2 text-xs text-slate-500">
                  Nessuna anomalia documentale
                </li>
              ) : (
                docs.map((a) => (
                  <li
                    key={a}
                    className="flex items-center justify-between gap-2 rounded-lg border border-amber-100 bg-amber-50/40 px-2.5 py-2 text-xs"
                  >
                    <span className="truncate text-slate-700">{String(a).replace(/_/g, " ")}</span>
                    <Badge variant="outline" className="shrink-0 border-amber-200 text-[10px] text-amber-800">
                      Richiesto
                    </Badge>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Azioni rapide
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate(carepyTo)}
              >
                <FileUp className="mr-1 h-3 w-3" />
                Vedi Carepy
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate(schedaTo)}
              >
                <Eye className="mr-1 h-3 w-3" />
                Apri terapia
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate(controlloTo)}
              >
                <ClipboardCheck className="mr-1 h-3 w-3" />
                Verifica ricette
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate(schedaTo)}
              >
                <FileText className="mr-1 h-3 w-3" />
                Documenti
              </Button>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Cronologia attività
            </p>
            <ol className="relative space-y-3 border-l border-slate-200 pl-3">
              {pratica.ultima_attivita ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-[#0F5C4C]" />
                  Ultima attività
                  <span className="mt-0.5 block text-[10px] text-slate-400">
                    {formatWhen(pratica.ultima_attivita)}
                  </span>
                </li>
              ) : null}
              {(pratica.carepy_count || 0) > 0 ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                  Carepy caricato ({pratica.carepy_count})
                </li>
              ) : null}
              {pratica.terapia_da_visionare ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-amber-400" />
                  Terapia da visionare
                </li>
              ) : null}
              {!pratica.ultima_attivita && !(pratica.carepy_count > 0) ? (
                <li className="text-xs text-slate-400">Nessuna attività recente</li>
              ) : null}
            </ol>
          </div>
        </div>

        <div className="mt-auto border-t border-slate-100 p-3">
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to={schedaTo}>
              Vai alla pratica completa
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

export default function FarmaciaPratiche() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pratiche, setPratiche] = useState([]);
  const [strutture, setStrutture] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutte");
  const [orgFilter, setOrgFilter] = useState("all");
  const [selectedKey, setSelectedKey] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [strRes, kpiRes, cliRes] = await Promise.all([
        farmGet("/farmacia/strutture", token, { lite: true }).catch(() => ({ items: [] })),
        farmGet("/farmacia/ricette/kpi", token).catch(() => ({})),
        farmGet("/farmacia/clienti", token).catch(() => ({ items: [] })),
      ]);
      const struttureItems = strRes.items || [];
      setStrutture(struttureItems);
      setKpi(kpiRes || {});

      const fromStrutture = [];
      await Promise.all(
        struttureItems.slice(0, 8).map(async (s) => {
          try {
            const pan = await farmGet("/farmacia/ricette/panoramica", token, {
              organization_id: s.organization_id,
            });
            const nomeStr = s.denominazione || s.nome || s.organization_id;
            for (const o of pan.tutti_ospiti || []) {
              fromStrutture.push({
                key: `s:${o.fascicolo_id || o.id}`,
                tipologia: "in_struttura",
                fascicolo_id: o.fascicolo_id,
                organization_id: s.organization_id,
                nome: o.ospite_nome || o.nome,
                cognome: o.ospite_cognome || o.cognome,
                codice_fiscale: o.codice_fiscale,
                struttura_nome: o.struttura_nome || nomeStr,
                stato: o.stato || "nessuna_pratica",
                terapia_da_visionare: !!o.terapia_da_visionare,
                terapia_presente: !!o.terapia_presente,
                carepy_count: o.carepy_count || 0,
                ricette_count: o.ricette_count || 0,
                ricette_medico_ricevute: !!o.ricette_medico_ricevute,
                richiesta_farmacia_ricevuta: !!o.richiesta_farmacia_ricevuta,
                richiesta_id: o.richiesta_id,
                anomalie_documentali: o.anomalie_documentali || [],
                medico: o.medico,
                ultima_attivita: o.ultima_attivita,
                ordini_aperti: o.ordini_aperti || 0,
              });
            }
          } catch {
            /* ignore */
          }
        })
      );

      const fromClienti = (cliRes.items || []).map((c) => ({
        key: `c:${c.id}`,
        tipologia: "cliente_diretto",
        cliente_id: c.id,
        nome: c.nome,
        cognome: c.cognome,
        codice_fiscale: c.codice_fiscale,
        struttura_nome: "Consegna a domicilio",
        stato: (c.terapie || []).length ? "attesa_medico" : "nessuna_pratica",
        terapia_da_visionare: false,
        terapia_presente: (c.terapie || []).length > 0,
        terapie_count: (c.terapie || []).length,
        carepy_count: 0,
        ricette_count: 0,
        ricette_medico_ricevute: false,
        richiesta_farmacia_ricevuta: false,
        anomalie_documentali: [],
        ultima_attivita: c.updated_at || c.created_at,
        has_carepy: false,
        columnHint: (c.terapie || []).length ? "ricette" : "carepy",
      }));

      const merged = [...fromStrutture, ...fromClienti];
      setPratiche(merged);
      setSelectedKey((prev) => {
        if (prev && merged.some((p) => p.key === prev)) return prev;
        return merged[0]?.key || null;
      });
    } catch {
      setPratiche([]);
      toast.error("Impossibile caricare le pratiche");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let list = pratiche;
    if (orgFilter !== "all") {
      list = list.filter((p) => p.organization_id === orgFilter);
    }
    if (filter === "in_struttura") list = list.filter((p) => p.tipologia === "in_struttura");
    if (filter === "clienti_diretti") list = list.filter((p) => p.tipologia === "cliente_diretto");
    if (filter === "documenti_richiesti") {
      list = list.filter((p) => (p.anomalie_documentali || []).length > 0);
    }
    if (filter === "completate") list = list.filter((p) => p.stato === "completata");
    if (filter === "da_verificare") {
      list = list.filter(
        (p) =>
          p.stato !== "completata" &&
          (p.terapia_da_visionare ||
            (p.anomalie_documentali || []).length > 0 ||
            !p.ricette_medico_ricevute)
      );
    }
    return list;
  }, [pratiche, filter, orgFilter]);

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    for (const p of filtered) {
      const col = assignColumn(p);
      map[col].push(p);
    }
    return map;
  }, [filtered]);

  const selected = pratiche.find((p) => p.key === selectedKey) || null;

  const confirmTerapia = async (fascicoloId) => {
    try {
      await farmPost(`/farmacia/ospiti/${encodeURIComponent(fascicoloId)}/terapia/presa-visione`, token);
      setPratiche((prev) => prev.map((pratica) => pratica.fascicolo_id === fascicoloId ? { ...pratica, terapia_da_visionare: false } : pratica));
      toast.success("Terapia visionata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Conferma presa visione non riuscita");
    }
  };

  const kpiValues = useMemo(() => {
    const carepy =
      kpi.carepy_da_verificare ?? byColumn.carepy?.length ?? 0;
    const terapie = kpi.terapie_da_visionare ?? byColumn.terapie?.length ?? 0;
    const ricette = kpi.ricette_mancanti ?? byColumn.ricette?.length ?? 0;
    const documenti =
      kpi.documenti_in_scadenza ?? byColumn.documenti?.length ?? 0;
    const completate = byColumn.completate?.length ?? 0;
    // totali: aperte + completate dalla board filtrata, o somma colonne
    const totali =
      (byColumn.carepy?.length || 0) +
      (byColumn.terapie?.length || 0) +
      (byColumn.ricette?.length || 0) +
      (byColumn.documenti?.length || 0) +
      completate;
    return { totali, carepy, terapie, ricette, documenti, completate };
  }, [kpi, byColumn]);

  const channelStats = useMemo(() => {
    const inStruttura = filtered.filter((p) => p.tipologia === "in_struttura").length;
    const clienti = filtered.filter((p) => p.tipologia === "cliente_diretto").length;
    return { inStruttura, clienti };
  }, [filtered]);

  const solleciti = useMemo(() => {
    const counts = {};
    for (const p of pratiche) {
      for (const a of p.anomalie_documentali || []) {
        const label = String(a).replace(/_/g, " ");
        counts[label] = (counts[label] || 0) + 1;
      }
    }
    if (kpi.sollecitabili) {
      counts["Da sollecitare (ricette)"] = kpi.sollecitabili;
    }
    return Object.entries(counts)
      .map(([label, n]) => ({ label, n }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 5);
  }, [pratiche, kpi]);

  const ultimeCompletate = useMemo(() => {
    return [...(byColumn.completate || [])]
      .sort((a, b) => String(b.ultima_attivita || "").localeCompare(String(a.ultima_attivita || "")))
      .slice(0, 5);
  }, [byColumn]);

  const exportCsv = () => {
    const header = ["Cognome", "Nome", "Tipologia", "Struttura", "Colonna", "Stato", "Ultima attività"];
    const lines = filtered.map((p) => {
      const col = assignColumn(p);
      return [
        p.cognome || "",
        p.nome || "",
        p.tipologia === "cliente_diretto" ? "Cliente diretto" : "In struttura",
        p.struttura_nome || "",
        COLUMNS.find((c) => c.id === col)?.label || col,
        p.stato || "",
        p.ultima_attivita || "",
      ];
    });
    const csv = [header, ...lines]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pratiche-farmacia-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Esportazione avviata");
  };

  if (searchParams.get("vista") === "copertura") {
    return <ControlloRicette embedded />;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <FarmaciaPageChrome>
          <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta
          </Button>
          <Button
            type="button"
            size="sm"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() => navigate("/farmacia/richieste")}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nuova pratica
          </Button>
        </FarmaciaPageChrome>
        <div className="flex w-fit gap-1 rounded-xl bg-slate-100 p-1 text-xs">
          <Link to="/farmacia/pratiche" className="rounded-lg bg-white px-3 py-1.5 font-semibold text-slate-900 shadow-sm">Flusso pratiche</Link>
          <Link to="/farmacia/pratiche?vista=copertura" className="rounded-lg px-3 py-1.5 text-slate-600 hover:text-slate-900">Verifica ricette</Link>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <KpiTile icon={ClipboardCheck} label="Pratiche totali" value={kpiValues.totali} tone="sky" />
          <KpiTile icon={HeartPulse} label="Carepy ricevuti" value={kpiValues.carepy} tone="emerald" />
          <KpiTile icon={Eye} label="Terapie da visionare" value={kpiValues.terapie} tone="amber" />
          <KpiTile icon={FileText} label="Ricette da verificare" value={kpiValues.ricette} tone="violet" />
          <KpiTile icon={FileWarning} label="Documenti richiesti" value={kpiValues.documenti} tone="teal" />
          <KpiTile icon={CheckCircle2} label="Completate" value={kpiValues.completate} tone="slate" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex max-w-full flex-wrap gap-1.5 rounded-xl bg-slate-100/80 p-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition sm:text-xs ${
                  filter === f.id
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select
              className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-xs text-slate-700"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
            >
              <option value="all">Tutte le strutture</option>
              {strutture.map((s) => (
                <option key={s.organization_id} value={s.organization_id}>
                  {s.denominazione || s.nome}
                </option>
              ))}
            </select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-xl"
              onClick={() => navigate("/farmacia/richieste")}
            >
              Carepy
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-xl"
              onClick={() => navigate("/farmacia/pratiche?vista=copertura")}
            >
              Verifica ricette
            </Button>
          </div>
        </div>

        <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm md:p-5">
          <div className="flex gap-3 overflow-x-auto pb-1">
            {COLUMNS.map((col) => {
              const items = byColumn[col.id] || [];
              const shown = items.slice(0, 5);
              return (
                <div key={col.id} className="w-[180px] shrink-0 sm:w-[190px]">
                  <div className="mb-2 flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${col.accent}`} />
                    <span className={`text-[11px] font-semibold ${col.header}`}>{col.label}</span>
                    <span className="ml-auto text-[11px] tabular-nums text-slate-400">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {shown.map((p) => {
                      const active = selectedKey === p.key;
                      const st = cardStatusLabel(p, col.id);
                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setSelectedKey(p.key)}
                          className={`w-full rounded-xl border p-2.5 text-left transition ${
                            active
                              ? "border-emerald-300 bg-emerald-50/60 shadow-sm"
                              : "border-slate-100 bg-slate-50/80 hover:border-slate-200 hover:bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-[10px] font-semibold text-white">
                              {initials(p.nome, p.cognome)}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold text-slate-800">
                                {p.cognome} {p.nome}
                              </p>
                              <p className="truncate text-[10px] text-slate-400">
                                {p.struttura_nome || "—"}
                              </p>
                            </div>
                          </div>
                          <p className={`mt-1.5 text-[10px] font-medium ${st.tone}`}>{st.text}</p>
                          {p.ultima_attivita ? (
                            <p className="mt-0.5 text-[10px] text-slate-400">
                              {formatWhen(p.ultima_attivita)}
                            </p>
                          ) : null}
                        </button>
                      );
                    })}
                    {items.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-200 px-2 py-6 text-center text-[10px] text-slate-400">
                        Nessuna
                      </div>
                    ) : null}
                    {items.length > 5 ? (
                      <p className="text-center text-[10px] text-slate-400">
                        + altre {items.length - 5} pratiche
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Solleciti e scadenze</h3>
            {solleciti.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun sollecito in coda</p>
            ) : (
              <ul className="space-y-2">
                {solleciti.map((s) => (
                  <li
                    key={s.label}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                      <span className="truncate font-medium text-slate-800">{s.label}</span>
                    </div>
                    <Badge variant="outline" className="shrink-0 border-amber-200 text-[10px] text-amber-800">
                      {s.n} in scadenza
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Pratiche per canale</h3>
            <ChannelDonut inStruttura={channelStats.inStruttura} clienti={channelStats.clienti} />
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Ultime pratiche completate</h3>
            {ultimeCompletate.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna pratica completata in elenco</p>
            ) : (
              <ul className="space-y-2">
                {ultimeCompletate.map((p) => (
                  <li
                    key={p.key}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">
                        {p.cognome} {p.nome}
                      </p>
                      <p className="truncate text-slate-400">{p.struttura_nome}</p>
                    </div>
                    <span className="shrink-0 text-slate-400">
                      {p.ultima_attivita ? formatWhen(p.ultima_attivita) : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {selected ? (
          <div className="xl:hidden">
            <PraticaDetailPanel pratica={selected} onClose={() => setSelectedKey(null)} onConfirmTerapia={confirmTerapia} />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <PraticaDetailPanel pratica={selected} onClose={() => setSelectedKey(null)} onConfirmTerapia={confirmTerapia} />
      </div>
    </div>
  );
}
